import React from 'react';
import { motion } from 'framer-motion';
import { XIcon, PackageIcon, BellIcon, CreditCardIcon, CheckCircle2Icon, PlugIcon, ArrowRightIcon } from 'lucide-react';

/**
 * Shown once, the moment a merchant switches on payments. Turning ordering on
 * adds a whole section to their workspace — this explains what arrived and
 * walks them straight into it rather than leaving them to find it.
 */
export function OrdersUnlockedModal({
  onClose,
  onOpenOrders
}: {
  onClose: () => void;
  onOpenOrders: () => void;
}) {
  const features = [{
    icon: BellIcon,
    title: 'Every order in one place',
    desc: 'New purchases, bookings and rentals arrive here — never buried in a chat thread.'
  }, {
    icon: CreditCardIcon,
    title: 'Payment shown separately',
    desc: 'You can see at a glance that an order is paid but still being prepared.'
  }, {
    icon: CheckCircle2Icon,
    title: 'Two taps to complete',
    desc: 'Start order, then mark it fulfilled. Talab records who did it and when.'
  }, {
    icon: PlugIcon,
    title: 'Works with your own system',
    desc: 'Already run a POS, ERP or dispatch tool? Connect it and statuses sync both ways.'
  }];
  return <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6" data-id="element-3130" data-mp="3lf2j5hkneutt">
      <div className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={onClose} data-id="element-3131" data-mp="3gky7wm4co0mx" />
      <motion.div initial={{
      opacity: 0,
      y: 16,
      scale: 0.96
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden" data-id="element-3132" data-mp="3ugaeprvs5hk7">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 p-2 rounded-lg text-slate-400 hover:text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-3133" data-mp="24zrdw2cplpdc">
          <XIcon className="w-4 h-4" data-id="element-3134" data-mp="dw9190l2sxe8" />
        </button>

        <div className="px-7 pt-8 pb-6" data-id="element-3135" data-mp="hx1fk88t1shd">
          <span className="w-12 h-12 rounded-2xl bg-primary-indigo flex items-center justify-center shadow-lg shadow-primary-indigo/25" data-id="element-3136" data-mp="hw9sjlt9x32p">
            <PackageIcon className="w-6 h-6 text-white" data-id="element-3137" data-mp="mtrrcjdku1wz" />
          </span>
          <p className="mt-5 text-[11px] font-bold uppercase tracking-wider text-primary-indigo" data-id="element-3138" data-mp="ruphpj8qq29k">
            New in your Business Hub
          </p>
          <h2 className="font-display font-bold text-2xl text-dark-navy mt-1.5 leading-tight" data-id="element-3139" data-mp="11lahvb42beul">
            Orders is now switched on
          </h2>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed" data-id="element-3140" data-mp="15m44m15rqnlw">
            Customers can now complete a purchase, booking or rental
            themselves — and everything they commit to lands in the new{' '}
            <span className="font-semibold text-dark-navy" data-id="element-3141" data-mp="1fip9mmhsfypq">Orders</span> tab
            beside Leads.
          </p>

          <div className="mt-6 space-y-4" data-id="element-3142" data-mp="1kpnc9da7wefq">
            {features.map(f => <div key={f.title} className="flex gap-3.5" data-id="element-3143" data-mp="1krqbmnx9fjx4">
                <span className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-3144" data-mp="1frlucvoj2l5u">
                  <f.icon className="w-[18px] h-[18px] text-primary-indigo" data-id="element-3145" data-mp="1br1ydvfoxnla" />
                </span>
                <div className="min-w-0" data-id="element-3146" data-mp="28hc996xzhr30">
                  <p className="text-sm font-bold text-dark-navy" data-id="element-3147" data-mp="23fmzrvqetlh1">{f.title}</p>
                  <p className="text-[13px] text-slate-500 mt-0.5 leading-relaxed" data-id="element-3148" data-mp="1ypewhvrbur26">
                    {f.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        <footer className="flex items-center justify-end gap-3 px-7 py-4 border-t border-slate-100 bg-slate-50/60" data-id="element-3149" data-mp="2r697tn0ez5r1">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-3150" data-mp="2mdy4ea6gt1tq">
            Later
          </button>
          <button onClick={onOpenOrders} className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3151" data-mp="2bxduaaivwqdw">
            Show me Orders
            <ArrowRightIcon className="w-4 h-4" data-id="element-3152" data-mp="2posayapbkfhs" />
          </button>
        </footer>
      </motion.div>
    </div>;
}